import React, { forwardRef } from 'react'
import { cn } from '@/utils/cn'

const variantStyles = {
  primary:
    'bg-[#F28C28] text-white hover:bg-portal-orange-hover focus:ring-portal-orange',
  secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-400',
  outline:
    'border-2 border-portal-orange text-portal-orange bg-transparent hover:bg-portal-orange hover:text-white focus:ring-portal-orange',
  ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-300',
} as const

const baseClasses =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold py-3.5 px-4 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'

export type ButtonVariant = keyof typeof variantStyles

export interface ButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> {
  children: React.ReactNode
  variant?: ButtonVariant
  backgroundColor?: string
  textColor?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  loading?: boolean
  loadingLabel?: string
  className?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      backgroundColor,
      textColor,
      icon,
      iconPosition = 'left',
      fullWidth,
      loading = false,
      loadingLabel = 'Loading...',
      disabled,
      type = 'button',
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled ?? loading
    const useCustomColors = Boolean(backgroundColor ?? textColor)
    const variantClass = useCustomColors ? '' : variantStyles[variant]

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading}
        className={cn(
          baseClasses,
          variantClass,
          useCustomColors && backgroundColor,
          useCustomColors && textColor,
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="h-5 w-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {loadingLabel}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className="shrink-0">{icon}</span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className="shrink-0">{icon}</span>
            )}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
